/**
 * Small SVG radar comparing a state's top-3 amenities (percent share) vs nationwide share.
 * `data`: [{amenity, share, nation_share}, ...]  (3 entries expected)
 */
export function AmenityRadar({ data, size = 120 }) {
  if (!Array.isArray(data) || data.length < 3) {
    return (
      <svg data-testid="amenity-radar" width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
        <text x={size / 2} y={size / 2} textAnchor="middle" fontSize="8" fill="#666" style={{ fontFamily: "monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>Needs 3 amenities</text>
      </svg>
    );
  }
  const rows = data.slice(0, 3);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 12;
  const angles = [-Math.PI / 2, -Math.PI / 2 + (2 * Math.PI) / 3, -Math.PI / 2 + (4 * Math.PI) / 3];

  // scale by max share seen (so radar fills nicely regardless of absolute %)
  const maxShare = Math.max(100, ...rows.flatMap((r) => [r.share || 0, r.nation_share || 0]));

  const point = (i, val) => {
    const rr = ((val || 0) / maxShare) * r;
    return [cx + Math.cos(angles[i]) * rr, cy + Math.sin(angles[i]) * rr];
  };
  const axisEnd = (i) => [cx + Math.cos(angles[i]) * r, cy + Math.sin(angles[i]) * r];

  const stateShape = rows.map((row, i) => point(i, row.share).join(",")).join(" ");
  const nationShape = rows.map((row, i) => point(i, row.nation_share).join(",")).join(" ");

  return (
    <svg data-testid="amenity-radar" width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      {/* concentric rings */}
      {[0.33, 0.66, 1].map((f) => (
        <circle key={f} cx={cx} cy={cy} r={r * f} fill="none" stroke="#222" strokeWidth="0.5" />
      ))}
      {/* axes */}
      {angles.map((_, i) => {
        const [ax, ay] = axisEnd(i);
        return <line key={i} x1={cx} y1={cy} x2={ax} y2={ay} stroke="#222" strokeWidth="0.5" />;
      })}
      {/* nationwide (grey) polygon */}
      <polygon points={nationShape} fill="rgba(255,255,255,0.06)" stroke="#888" strokeWidth="1" strokeDasharray="2 2" />
      {/* state (red) polygon */}
      <polygon points={stateShape} fill="rgba(255,59,48,0.25)" stroke="#FF3B30" strokeWidth="1.4" />
      {/* labels */}
      {rows.map((row, i) => {
        const [lx, ly] = axisEnd(i);
        const anchor = i === 0 ? "middle" : (lx < cx ? "end" : "start");
        return (
          <text key={row.amenity} x={lx} y={ly + (i === 0 ? -3 : 8)} textAnchor={anchor} fontSize="7" fill="#999" style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}>
            {row.amenity.length > 10 ? row.amenity.slice(0, 10) + "…" : row.amenity}
          </text>
        );
      })}
    </svg>
  );
}
